// CloudKeeper Org Services Portal — Amazon Macie API client.
// Uses portalFetch from portalApi.js (JWT stored as portal_token).
// Mirrors guarddutyApi.js: every call resolves to the {success,data} envelope.
import { portalFetch } from './portalApi';

const BASE = '/api/v1/securityhub/macie';

// --- config ---
export const getMacieConfig = () =>
  portalFetch(`${BASE}/config`);

export const enableMacie = (body) =>
  portalFetch(`${BASE}/config`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const disableMacie = () =>
  portalFetch(`${BASE}/config`, { method: 'DELETE' });

export const updateMacieRegions = (linkingRegions) =>
  portalFetch(`${BASE}/config/regions`, {
    method: 'PUT',
    body: JSON.stringify({ linkingRegions }),
  });

export const setMacieDiscovery = (enabled) =>
  portalFetch(`${BASE}/config/discovery`, {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  });

// --- members ---
export const listMacieMembers = () =>
  portalFetch(`${BASE}/members`);

export const addMacieMembers = (accountIds, memberEmails) =>
  portalFetch(`${BASE}/members`, {
    method: 'POST',
    body: JSON.stringify({ accountIds, memberEmails }),
  });

export const removeMacieMember = (accountId) =>
  portalFetch(`${BASE}/members/${encodeURIComponent(accountId)}`, {
    method: 'DELETE',
  });

// Macie members are per (account, region); retry targets one cell.
export const retryMacieMember = ({ accountId, region }) =>
  portalFetch(`${BASE}/members/retry`, {
    method: 'POST',
    body: JSON.stringify({ accountId, region }),
  });

// --- member emails ---
// Each account's root-email lookup is a per-account ops-service round-trip on the
// backend. Resolving a large selection in one request can exceed the gateway
// timeout (504), so split into small batches and fire them sequentially — the next
// batch starts as soon as the previous returns. Results are merged into the same
// { data: { results } } shape callers already expect.
const RESOLVE_CHUNK_SIZE = 5;

export const resolveMacieMemberEmails = async (accountIds) => {
  const ids = accountIds || [];
  const results = [];
  for (let i = 0; i < ids.length; i += RESOLVE_CHUNK_SIZE) {
    const chunk = ids.slice(i, i + RESOLVE_CHUNK_SIZE);
    // eslint-disable-next-line no-await-in-loop -- intentional: one batch at a time
    const res = await portalFetch(`${BASE}/member-emails/resolve`, {
      method: 'POST',
      body: JSON.stringify({ accountIds: chunk }),
    });
    results.push(...(res?.data?.results || []));
  }
  return { data: { results } };
};

// --- findings ---
export const listMacieFindings = (filters = {}) => {
  const qs = new URLSearchParams();
  // Repeated array params joined with "," — mirror guarddutyApi.listGuardDutyFindings.
  if (filters.severity?.length) qs.set('severity', filters.severity.join(','));
  if (filters.archived !== undefined && filters.archived !== null) qs.set('archived', String(filters.archived)); // handler reads `archived` (there is no workflowStatus param server-side)
  if (filters.account?.length) qs.set('account', filters.account.join(','));
  if (filters.category?.length) qs.set('category', filters.category.join(','));
  if (filters.type) qs.set('type', filters.type);
  if (filters.search) qs.set('search', filters.search);
  if (filters.cursor) qs.set('cursor', filters.cursor);
  if (filters.limit) qs.set('limit', String(filters.limit));
  const q = qs.toString();
  return portalFetch(`${BASE}/findings${q ? `?${q}` : ''}`);
};

export const getMacieFinding = (id) =>
  portalFetch(`${BASE}/findings/${encodeURIComponent(id)}`);

// Prism-local archive toggle (macie2 has no per-finding archive API).
export const updateMacieWorkflow = ({ findingIds, archive }) =>
  portalFetch(`${BASE}/findings/workflow`, {
    method: 'POST',
    body: JSON.stringify({ findingIds, archive }),
  });

// --- refresh (kind: 'findings' | 'buckets') ---
export const triggerMacieRefresh = (kind) =>
  portalFetch(`${BASE}/refresh`, {
    method: 'POST',
    body: JSON.stringify({ kind }),
  });

// Currently running refresh job of the given kind for this tenant (data: null when
// none) — lets a dashboard re-attach its progress bar after navigation or reload.
export const getMacieActiveRefreshJob = (kind) => {
  const qs = new URLSearchParams();
  if (kind) qs.set('kind', kind);
  const q = qs.toString();
  return portalFetch(`${BASE}/refresh/active${q ? `?${q}` : ''}`);
};

export const getMacieRefreshJob = (jobId) =>
  portalFetch(`${BASE}/refresh/${encodeURIComponent(jobId)}`);

// --- summary ---
export const getMaciePostureSummary = () =>
  portalFetch(`${BASE}/summary`);

// --- role permissions ("Fix roles") ---
// Prism keeps a scoped inline policy on each enrolled account's switch role. The
// scan is async: POST starts (or re-joins) a job, GET polls it. Fix is
// fire-and-forget — re-run a scan to see the outcome.
export const startMacieRolesScan = () =>
  portalFetch(`${BASE}/roles/status`, { method: 'POST' });

export const getMacieRolesJob = (jobId) =>
  portalFetch(`${BASE}/roles/status/${encodeURIComponent(jobId)}`);

export const fixMacieRoles = () =>
  portalFetch(`${BASE}/roles/fix`, { method: 'POST' });

// --- S3 bucket posture ---
export const listMacieBuckets = (filters = {}) => {
  const qs = new URLSearchParams();
  if (filters.account) qs.set('account', filters.account);
  if (filters.public) qs.set('public', filters.public);
  if (filters.shared) qs.set('shared', filters.shared);
  if (filters.encryption) qs.set('encryption', filters.encryption);
  if (filters.search) qs.set('search', filters.search);
  const q = qs.toString();
  return portalFetch(`${BASE}/buckets${q ? `?${q}` : ''}`);
};
